import React, { useState, useEffect } from 'react';
import { Menu, X, Linkedin, Instagram, Facebook } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { companyInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Products', href: '#products' },
    { name: 'Process', href: '#process' },
    { name: 'Blog', href: '/blog', isRoute: true },
    { name: 'Certificates', href: '/certificates', isRoute: true },
    { name: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (e, href, isRoute) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    if (isRoute) {
      // For routes like /blog, /certificates
      navigate(href);
    } else {
      // For section anchors (#about, #products, etc.)
      const currentPath = window.location.pathname;
      
      if (currentPath !== '/') {
        // Not on homepage - navigate to homepage first, then scroll
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        // Already on homepage - just scroll
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-md' 
          : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="w-14 h-14 rounded-full overflow-hidden bg-white shadow-md">
              <img 
                src={companyInfo.logo} 
                alt={companyInfo.name}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-amber-950">{companyInfo.name}</h1>
              <p className="text-xs text-green-700 font-medium">{companyInfo.tagline}</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href, link.isRoute)}
                className="text-gray-700 hover:text-green-700 font-medium transition-colors duration-200 relative group"
              >
                {link.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-700 group-hover:w-full transition-all duration-300"></span>
              </a>
            ))}
          </nav>

          {/* Social Media Icons & CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Social Media Icons */}
            <div className="flex items-center space-x-3 mr-2">
              <a
                href="https://www.linkedin.com/company/cornerstone-exim"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-600 transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="https://www.instagram.com/cornerstone_exim?igsh=NTJubGpra2V5ZGNv&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-pink-600 transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.facebook.com/share/1DfMbdEaY6/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-700 transition-colors duration-200"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
            </div>
            
            {/* CTA Button */}
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              className="bg-green-700 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-green-800 transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Get Quote
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-gray-700 hover:text-green-700 transition-colors"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <nav className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href, link.isRoute)}
                className="block py-2 text-gray-700 hover:text-green-700 font-medium transition-colors"
              >
                {link.name}
              </a>
            ))}
            
            {/* Social Media Icons - Mobile */}
            <div className="flex items-center justify-center space-x-6 py-4 border-t border-gray-200">
              <a
                href="https://www.linkedin.com/company/cornerstone-exim"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-600 transition-colors duration-200"
                aria-label="LinkedIn"
              >
                <Linkedin size={24} />
              </a>
              <a
                href="https://www.instagram.com/cornerstone_exim?igsh=NTJubGpra2V5ZGNv&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-pink-600 transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram size={24} />
              </a>
              <a
                href="https://www.facebook.com/share/1DfMbdEaY6/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-700 transition-colors duration-200"
                aria-label="Facebook"
              >
                <Facebook size={24} />
              </a>
            </div>
            
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              className="block w-full bg-green-700 text-white px-6 py-2.5 rounded-lg font-semibold text-center hover:bg-green-800 transition-colors"
            >
              Get Quote
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
